import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Table, Alert, Spinner, Modal } from 'react-bootstrap';
import { FiArrowLeft, FiCloudRain, FiCalendar, FiMapPin, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { getActivityForecastById, createCheckClaimFromForecast } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import WeatherDataSections from '../../components/Common/WeatherDataSections';
import PolicySumInsuredRows from '../../components/Common/PolicySumInsuredFields';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const AdminActivityForecastDetail = () => {
    const navigate = useNavigate();
    const { forecastId } = useParams();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [forecast, setForecast] = useState(location.state?.forecast || null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [processingCheckClaim, setProcessingCheckClaim] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        const fetchForecast = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getActivityForecastById(forecastId);
                // API returns { forecast_details: { ... } }
                setForecast(data?.forecast_details || data?.forecast || data || null);
            } catch (e) {
                setError(handleApiError(e, 'Erreur lors du chargement de la prévision'));
            } finally {
                setLoading(false);
            }
        };
        // Always fetch by id to ensure up-to-date data
        if (forecastId) fetchForecast();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forecastId]);

    if (loading) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de la prévision</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement de la prévision...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de la prévision</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <Alert variant="danger">
                            <FiAlertTriangle className="me-2" />
                            {error}
                        </Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!forecast) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de la prévision</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée de prévision disponible pour l'identifiant: {forecastId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const get = (obj, path, fallback = '—') => {
        try {
            const v = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
            return v ?? fallback;
        } catch {
            return fallback;
        }
    };

    const handleOpenConfirmModal = () => {
        setShowConfirmModal(true);
    };

    const handleCloseConfirmModal = () => {
        setShowConfirmModal(false);
    };

    const handleCreateCheckClaim = async () => {
        const forecastId = forecast?.forecast_id;
        if (!forecastId) {
            setErrorMessage('ID de prévision manquant');
            handleCloseConfirmModal();
            return;
        }

        try {
            setProcessingCheckClaim(true);
            setErrorMessage(null);
            setSuccessMessage(null);
            handleCloseConfirmModal();

            await createCheckClaimFromForecast(forecastId);

            const policyCode = get(forecast, ['policy_info', 'policy_code'], '');
            setSuccessMessage(
                `Check claim créé avec succès pour la prévision ${policyCode ? `(Police: ${policyCode})` : forecastId}`
            );
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (error) {
            let errorMsg = 'Erreur lors de la création du check claim';

            // Extraire le message d'erreur du backend
            if (error.response?.data) {
                const responseData = error.response.data;

                // Gérer le cas spécifique où un check claim existe déjà
                if (error.response.status === 400 && responseData.detail) {
                    const detail = responseData.detail;
                    if (typeof detail === 'string' && detail.includes('already exists')) {
                        // Formater le message pour qu'il soit plus convivial
                        const forecastIdMatch = detail.match(/forecast_id\s+([a-f0-9-]+)/i);
                        const policyCode = get(forecast, ['policy_info', 'policy_code'], '');
                        if (forecastIdMatch) {
                            errorMsg = `Un check claim existe déjà pour cette prévision${policyCode ? ` (Police: ${policyCode})` : ''}.`;
                        } else {
                            errorMsg = 'Un check claim existe déjà pour cette prévision.';
                        }
                    } else if (typeof detail === 'string') {
                        errorMsg = detail;
                    } else {
                        errorMsg = handleApiError(error, errorMsg);
                    }
                } else {
                    errorMsg = handleApiError(error, errorMsg);
                }
            } else if (error.message) {
                errorMsg = error.message;
            } else {
                errorMsg = handleApiError(error, errorMsg);
            }

            setErrorMessage(errorMsg);
            setTimeout(() => setErrorMessage(null), 6000);
        } finally {
            setProcessingCheckClaim(false);
        }
    };

    const policyInfo = forecast.policy_info || {};
    const activityInfo = forecast.activity_info || {};
    const clientInfo = forecast.client_info || {};
    const partnerInfo = forecast.partner_info || {};
    const referenceInfo = forecast.reference_info || {};
    const weatherData = forecast.weather_data || [];
    const weatherTemp = forecast.weather_temp || [];
    const weatherWind = forecast.weather_wind || [];

    return (
        <div className="dashboard-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="page-title">
                    <FiCloudRain className="me-2" />
                    Détail de la prévision d'activité
                </h2>
                <div className="d-flex gap-2">
                    <Button
                        variant="success"
                        onClick={handleOpenConfirmModal}
                        disabled={processingCheckClaim || !forecast?.forecast_id}
                        className="d-flex align-items-center"
                    >
                        {processingCheckClaim ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Création en cours...
                            </>
                        ) : (
                            <>
                                <FiCheckCircle className="me-2" />
                                Créer check claim
                            </>
                        )}
                    </Button>
                    <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                        <FiArrowLeft className="me-2" /> Retour
                    </Button>
                </div>
            </div>

            {successMessage && (
                <Alert variant="success" className="mt-3" onClose={() => setSuccessMessage(null)} dismissible>
                    <strong>Succès !</strong> {successMessage}
                </Alert>
            )}

            {errorMessage && (
                <Alert variant="danger" className="mt-3" onClose={() => setErrorMessage(null)} dismissible>
                    <strong>Erreur !</strong> {errorMessage}
                </Alert>
            )}

            {/* Modal de confirmation */}
            <Modal show={showConfirmModal} onHide={handleCloseConfirmModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FiCheckCircle className="me-2" />
                        Confirmer la création du check claim
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Êtes-vous sûr de vouloir créer un check claim pour cette prévision ?</p>
                    {forecast && (
                        <div className="mt-3 p-3 bg-light rounded">
                            <p className="mb-1"><strong>Code police :</strong> {get(forecast, ['policy_info', 'policy_code'], '—')}</p>
                            <p className="mb-1"><strong>Date prévision :</strong> {get(forecast, ['forecast_date'], '—')}</p>
                            <p className="mb-1"><strong>Ville :</strong> {get(forecast, ['activity_info', 'activity_city'], '—')}</p>
                            <p className="mb-0"><strong>Total précipitation :</strong> {get(forecast, ['total_mm_prc'], '0')} mm</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseConfirmModal}>
                        Annuler
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleCreateCheckClaim}
                        disabled={processingCheckClaim}
                    >
                        {processingCheckClaim ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Création en cours...
                            </>
                        ) : (
                            <>
                                <FiCheckCircle className="me-2" />
                                Confirmer
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Informations prévision */}
            <Section title="Informations prévision">
                <Row className="mb-2">
                    <Col md={4}><Label>ID Prévision</Label><Value>{get(forecast, ['forecast_id'])}</Value></Col>
                    <Col md={4}><Label>Date de prévision</Label><Value><FiCalendar className="me-1" />{get(forecast, ['forecast_date'])}</Value></Col>
                    <Col md={4}><Label>Source de données</Label><Value>{get(forecast, ['data_source'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Coordonnées</Label><Value><FiMapPin className="me-1" />{get(forecast, ['latitude'])}, {get(forecast, ['longitude'])}</Value></Col>
                    <Col md={4}><Label>Adresse</Label><Value>{get(forecast, ['address'])}</Value></Col>
                    <Col md={4}><Label>Total mm précipitation</Label><Value>{get(forecast, ['total_mm_prc'])} mm</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Année d'initialisation</Label><Value>{get(forecast, ['meteomatics_init_year'])}</Value></Col>
                    <Col md={4}><Label>Mois d'initialisation</Label><Value>{get(forecast, ['meteomatics_init_month'])}</Value></Col>
                    <Col md={4}><Label>Jour d'initialisation</Label><Value>{get(forecast, ['meteomatics_init_day'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Heure UTC</Label><Value>{get(forecast, ['meteomatics_run_hour_utc'])}</Value></Col>
                    <Col md={4}><Label>Heure Paris</Label><Value>{get(forecast, ['meteomatics_run_hour_paris'])}</Value></Col>
                    <Col md={4}><Label>Dernière modification</Label><Value>{get(forecast, ['forecast_last_modified_at'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Modifié par</Label><Value>{get(forecast, ['forecast_last_modified_by'])}</Value></Col>
                </Row>
            </Section>

            {/* Informations police */}
            <Section title="Informations police">
                <Row className="mb-2">
                    <Col md={4}><Label>Code police</Label><Value>{get(policyInfo, ['policy_code'])}</Value></Col>
                    <Col md={4}><Label>Statut</Label><Value><Badge bg="info">{get(policyInfo, ['status'])}</Badge></Value></Col>
                    <Col md={4}><Label>Date UW</Label><Value><FiCalendar className="me-1" />{get(policyInfo, ['uw_date'])}</Value></Col>
                </Row>
                <PolicySumInsuredRows source={policyInfo} Label={Label} Value={Value} colMd={6} />
            </Section>

            {/* Informations activité */}
            <Section title="Informations activité">
                <Row className="mb-2">
                    <Col md={4}><Label>Code activité</Label><Value>{get(activityInfo, ['activity_code'])}</Value></Col>
                    <Col md={4}><Label>Nom</Label><Value>{get(activityInfo, ['name'])}</Value></Col>
                    <Col md={4}><Label>Catégorie</Label><Value>{get(activityInfo, ['category'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Date début</Label><Value><FiCalendar className="me-1" />{get(activityInfo, ['activity_start_date'])}</Value></Col>
                    <Col md={4}><Label>Heure début</Label><Value>{get(activityInfo, ['activity_start_time'])}</Value></Col>
                    <Col md={4}><Label>Date fin</Label><Value><FiCalendar className="me-1" />{get(activityInfo, ['activity_end_date'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Heure fin</Label><Value>{get(activityInfo, ['activity_end_time'])}</Value></Col>
                    <Col md={4}><Label>Durée</Label><Value>{get(activityInfo, ['duration'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Adresse</Label><Value>{get(activityInfo, ['activity_address'])}</Value></Col>
                    <Col md={4}><Label>Ville</Label><Value>{get(activityInfo, ['activity_city'])}</Value></Col>
                    <Col md={4}><Label>Code postal</Label><Value>{get(activityInfo, ['activity_zip_code'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Pays</Label><Value>{get(activityInfo, ['activity_country'])}</Value></Col>
                    <Col md={4}><Label>Prix</Label><Value>{get(activityInfo, ['activity_price'])}</Value></Col>
                    <Col md={4}><Label>Numéro de réservation</Label><Value>{get(activityInfo, ['reservation_number'])}</Value></Col>
                </Row>
            </Section>

            {/* Informations client */}
            <Section title="Informations client">
                <Row className="mb-2">
                    <Col md={4}><Label>Code client</Label><Value>{get(clientInfo, ['client_code'])}</Value></Col>
                    <Col md={4}><Label>Prénom</Label><Value>{get(clientInfo, ['first_name'])}</Value></Col>
                    <Col md={4}><Label>Nom</Label><Value>{get(clientInfo, ['last_name'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Email</Label><Value>{get(clientInfo, ['email'])}</Value></Col>
                    <Col md={4}><Label>Téléphone</Label><Value>{get(clientInfo, ['phone_number'])}</Value></Col>
                    <Col md={4}><Label>Statut</Label><Value><Badge bg={get(clientInfo, ['status']) === 'Active' ? 'success' : 'secondary'}>{get(clientInfo, ['status'])}</Badge></Value></Col>
                </Row>
            </Section>

            {/* Informations partenaire */}
            <Section title="Informations partenaire">
                <Row className="mb-2">
                    <Col md={4}><Label>Code partenaire</Label><Value>{get(partnerInfo, ['partner_code'])}</Value></Col>
                    <Col md={4}><Label>Nom de l'entreprise</Label><Value>{get(partnerInfo, ['company_name'])}</Value></Col>
                    <Col md={4}><Label>Prénom</Label><Value>{get(partnerInfo, ['partner_first_name'])}</Value></Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}><Label>Nom</Label><Value>{get(partnerInfo, ['partner_last_name'])}</Value></Col>
                    <Col md={4}><Label>Email</Label><Value>{get(partnerInfo, ['partner_email'])}</Value></Col>
                    <Col md={4}><Label>Vérifié</Label><Value><Badge bg={get(partnerInfo, ['partner_is_verified']) ? 'success' : 'secondary'}>{get(partnerInfo, ['partner_is_verified']) ? 'Oui' : 'Non'}</Badge></Value></Col>
                </Row>
            </Section>

            {/* Informations référence */}
            {referenceInfo && Object.keys(referenceInfo).length > 0 && (
                <Section title="Informations référence">
                    <Row className="mb-2">
                        <Col md={4}><Label>Code référence</Label><Value>{get(referenceInfo, ['reference_code'])}</Value></Col>
                        <Col md={4}><Label>Nom</Label><Value>{get(referenceInfo, ['reference_name'])}</Value></Col>
                        <Col md={4}><Label>Catégorie</Label><Value>{get(referenceInfo, ['reference_category'])}</Value></Col>
                    </Row>
                    <Row className="mb-2">
                    </Row>
                </Section>
            )}

            {/* Données météorologiques */}
            <WeatherDataSections
                weatherData={weatherData}
                weatherTemp={weatherTemp}
                weatherWind={weatherWind}
                enableCopy
                idPrefix="activity-forecast"
            />
        </div>
    );
};

export default AdminActivityForecastDetail;
